/*
 * Keeps cart line items on the correct Skio selling plan as quantities change.
 *
 * Rule (same as the buy widgets): the plan set follows the live total of
 * subscription bags in the cart — 1 bag → single-bag plan (no recurring
 * discount), 2+ bags → 2+ bags plan (20% recurring). Each line keeps its
 * delivery frequency (30/60/90 days) when it moves between sets.
 *
 * Reads the configured plan IDs from a [data-skio-cart-plans] element
 * (Theme Settings → Skio Selling Plans). Does nothing when unconfigured,
 * and never touches lines whose selling plan isn't one of the configured IDs.
 */
(() => {
  if (window.__skioCartPlanSyncLoaded) return;
  window.__skioCartPlanSyncLoaded = true;

  const FREQUENCIES = ['30', '60', '90'];
  const MAX_PASSES = 6; // safety bound; one pass per line change is typical

  /* ── Provisional-price mask ───────────────────────────────────────────────
   * A quantity change that crosses the 1↔2 bag boundary takes TWO round trips:
   * Dawn changes the quantity and paints the new section (assets/cart.js paints
   * at :183, publishes cartUpdate only at :220), and the swap below moves the
   * line onto the right plan and re-renders after that. In between, the cart
   * genuinely still holds the old plan, so Dawn faithfully paints a price that
   * is wrong — a lone bag wearing the 2+ bag discount, or a second bag still at
   * full price. Measured against the dev server, that figure sits on screen for
   * ~1.6s, which is long enough to read.
   *
   * Dawn's own spinner is no use as a signal: `disableLoading()` runs in the
   * same `.finally()` that paints, so it clears at the exact moment the wrong
   * number appears. So we raise our own mask on the shopper's click — before
   * Dawn's request even leaves — and lower it once this sync has settled.
   *
   * The flag lives on <html> deliberately: `onCartUpdate()` replaces
   * `.drawer__inner`'s innerHTML wholesale, so a flag parked inside the drawer
   * would be wiped mid-flight.
   */
  const MASK_ATTR = 'data-ml-price-sync';

  /* Failsafe only. If Dawn's request fails outright, cartUpdate never fires and
   * nothing below ever releases the mask, so it has to time out — otherwise the
   * prices stay hidden behind the cart error banner. Set well clear of a
   * legitimate multi-pass sync (each pass is 2 requests) so a slow connection
   * doesn't drop the mask early and flash the stale price after all. */
  const MASK_MAX_MS = 6000;
  let maskTimer = null;

  function holdPrices() {
    document.documentElement.setAttribute(MASK_ATTR, '');
    clearTimeout(maskTimer);
    maskTimer = setTimeout(releasePrices, MASK_MAX_MS);
  }

  function releasePrices() {
    clearTimeout(maskTimer);
    maskTimer = null;
    document.documentElement.removeAttribute(MASK_ATTR);
  }

  /* Capture phase: this has to win the race against Dawn's own click handler,
   * which fires on the same event and starts the request. Every control here
   * can move the cart across the 1↔2 boundary — the steppers and remove button
   * by changing counts, "Subscribe & Save" and the flavour upsell by adding a
   * managed line. Whether the boundary is actually crossed isn't knowable
   * without reading the cart, so this masks on every one; when no swap turns out
   * to be needed the mask lasts one /cart.js (~200ms) and lands inside Dawn's
   * spinner anyway. */
  const MASK_TRIGGERS = '.ml-qty button, cart-remove-button button, [data-subscribe-line], .ml-cart-upsell__button';

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest(MASK_TRIGGERS)) holdPrices();
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('.ml-qty')) holdPrices();
    },
    true
  );

  // Accepts a raw ID ("1234567890") or a GID ("gid://shopify/SellingPlan/1234567890")
  function normalizePlanId(value) {
    const matches = String(value || '').match(/\d+/g);
    return matches && matches.length ? parseInt(matches[matches.length - 1], 10) : null;
  }

  function getPlanSettings() {
    const holder = document.querySelector('[data-skio-cart-plans]');
    if (!holder) return null;
    let parsed;
    try {
      parsed = JSON.parse(holder.getAttribute('data-skio-cart-plans'));
    } catch (e) {
      console.error('Skio cart sync: invalid data-skio-cart-plans', e);
      return null;
    }
    const result = { single: {}, multi: {} };
    let hasAny = false;
    ['single', 'multi'].forEach((set) => {
      FREQUENCIES.forEach((freq) => {
        const id = normalizePlanId(parsed && parsed[set] ? parsed[set][freq] : null);
        if (id) {
          result[set][freq] = id;
          hasAny = true;
        }
      });
    });
    return hasAny ? result : null;
  }

  // Map of plan ID → {set: 'single'|'multi', freq: '30'|'60'|'90'}
  function buildPlanLookup(settings) {
    const lookup = new Map();
    ['single', 'multi'].forEach((set) => {
      FREQUENCIES.forEach((freq) => {
        const id = settings[set][freq];
        if (id && !lookup.has(id)) lookup.set(id, { set, freq });
      });
    });
    return lookup;
  }

  function fetchCart() {
    return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  // Returns the first line that needs its selling plan swapped, or null
  function computeNextChange(cart, settings, lookup) {
    const managed = (cart.items || []).filter((item) => {
      const planId = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? item.selling_plan_allocation.selling_plan.id
        : null;
      return planId && lookup.has(planId);
    });
    if (!managed.length) return null;

    const totalBags = managed.reduce((sum, item) => sum + item.quantity, 0);
    const desiredSet = totalBags >= 2 ? 'multi' : 'single';

    for (const item of managed) {
      const currentId = item.selling_plan_allocation.selling_plan.id;
      const { freq } = lookup.get(currentId);
      const set = settings[desiredSet];
      const targetId = set[freq] || set['30'] || set['60'] || set['90'];
      if (targetId && targetId !== currentId) {
        return { key: item.key, quantity: item.quantity, sellingPlanId: targetId };
      }
    }
    return null;
  }

  function applyChange(change) {
    const config = typeof fetchConfig === 'function'
      ? fetchConfig()
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    return fetch(routes.cart_change_url, {
      ...config,
      body: JSON.stringify({
        id: change.key,
        quantity: change.quantity,
        selling_plan: change.sellingPlanId,
      }),
    }).then((r) => r.json());
  }

  function refreshCartUI() {
    const jobs = [];

    const drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems && typeof drawerItems.onCartUpdate === 'function') {
      jobs.push(drawerItems.onCartUpdate());
    }

    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.onCartUpdate === 'function') {
      jobs.push(cartItems.onCartUpdate());

      // onCartUpdate only refreshes the items list; totals live in the footer
      const footer = document.getElementById('main-cart-footer');
      if (footer && footer.dataset.id) {
        jobs.push(
          fetch(`${routes.cart_url}?section_id=${footer.dataset.id}`)
            .then((r) => r.text())
            .then((text) => {
              const html = new DOMParser().parseFromString(text, 'text/html');
              const source = html.querySelector('.js-contents');
              const target = footer.querySelector('.js-contents');
              if (source && target) target.innerHTML = source.innerHTML;
            })
        );
      }
    }

    return Promise.all(jobs).catch((e) => console.error('Skio cart sync: UI refresh failed', e));
  }

  let syncing = false;
  let pendingSync = false;

  async function syncCartPlans() {
    const settings = getPlanSettings();
    // Unconfigured: no swap is coming, so nothing justifies holding the prices.
    if (!settings) {
      releasePrices();
      return;
    }

    // Don't release here — the in-flight run owns the mask and will release it
    // once it has picked up this pending pass.
    if (syncing) {
      pendingSync = true;
      return;
    }
    syncing = true;

    try {
      const lookup = buildPlanLookup(settings);
      let changed = false;

      // Re-fetch between passes: swapping a plan changes the line's key and
      // can merge lines, so keys from a previous pass may no longer exist.
      for (let pass = 0; pass < MAX_PASSES; pass++) {
        const cart = await fetchCart();
        const change = computeNextChange(cart, settings, lookup);
        if (!change) break;

        const state = await applyChange(change);
        if (state && state.status) {
          console.error('Skio cart sync: cart change failed', state);
          break;
        }
        changed = true;
      }

      if (changed) await refreshCartUI();
    } catch (e) {
      console.error('Skio cart sync: failed', e);
    } finally {
      syncing = false;
      if (pendingSync) {
        pendingSync = false;
        syncCartPlans();
      } else {
        // `refreshCartUI()` is awaited above, so by here the corrected prices
        // are already in the DOM and the mask can come down without a flash of
        // the stale figure. On the error path we release too: a visible wrong
        // price beats prices that never come back.
        releasePrices();
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      // Fires after cart-page/drawer quantity changes, removals, and widget adds
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => syncCartPlans());
    }

    // Heal carts that loaded in an inconsistent state (e.g. a previously failed sync)
    if (document.querySelector('.cart-item')) syncCartPlans();
  });
})();
